import { IconAlertCircle, Kicker } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type {
  AdminHive,
  AdminHiveAlert,
  AdminHiveLane,
  AdminHiveStat,
  AdminHiveWriter,
} from '../lib/admin';
import { absolute } from '../lib/time';
import { EmptyState } from './EmptyState';

// THE HIVE BAND (`docs/idea/hive.md`, plan 102 slice 10) — one row per fan-out lane,
// every stat as `rate (n / d)`.
//
// It replaces `AdminScoutSurveyBand`, which graded ONE lane for ONE tenant. Seven slices
// of this arc shipped producers for `team_fanout`, `review_fanout`, `devops_fanout`,
// `team_fix_wave`, `lead_idle_ms`, `steerable`, `falsified` and `handoffs_*`, and nothing
// anywhere read one of them; hive.md closes by asking for exactly this page.
//
// THREE RENDERINGS, AND THEY MUST STAY THREE. The server sends a discriminated verdict
// per stat and this presenter keeps them apart all the way to the pixel:
//
//   - `graded`          -> `33.3%` with `4 / 12` under it.
//   - `no_denominator`  -> "no data in this window". NEVER `0%` — a rate over nothing
//                          measured reads as healthy, which is the `cost-is-a-ceiling`
//                          defect wearing a percentage.
//   - `not_on_contract` -> "not on this lane". `attempts` rides `team_fanout` alone, and
//                          a 0% retry rate on the scout lane would be a measurement of a
//                          field its producer was never asked to write.
//
// AND THE ONE THING THIS BAND EXISTS FOR: a lane carrying an ALERT renders it as an
// alert, not as a blank. `steerable_rate` of 0 with members dispatched is precisely what
// a dead producer looks like — the mechanism ran, and the observable hop reported nothing.
// That is this repo's most recurrent defect class, and it was previously invisible
// everywhere.

// ponytail: the copy's locale (`t.locale`); a `lib/time.ts` number seam would retire these.
const PCT = new Intl.NumberFormat(t.locale, { style: 'percent', maximumFractionDigits: 1 });
const NUM = new Intl.NumberFormat(t.locale);

/** Lane label, via `admin.hive.lane.<event>`, falling back to the raw event name. */
function laneLabel(lane: string): string {
  const key = `admin.hive.lane.${lane}`;
  return t.has(key) ? t(key) : lane;
}

/**
 * One alert as a sentence naming its own number. The count rides so the band says
 * "0 of 41 members reachable" rather than flagging a lane and leaving the reader to hunt
 * for the denominator — a flag with no figure is the thing an operator ignores twice.
 */
function alertText(a: AdminHiveAlert): string {
  if (a.kind === 'steerable_dead') return t('admin.hive.alert_steerable_dead', { n: a.dispatched });
  if (a.kind === 'steered_dead') return t('admin.hive.alert_steered_dead', { n: a.steerable });
  return t('admin.hive.alert_no_row_parsed', { n: a.events });
}

/**
 * Which build WROTE a refused row. A `stamped` version is the fact itself; the two
 * unstamped kinds are named, not folded into one "unattributed" — a row a pre-#3062
 * binary stamped empties out of the window by itself, while a lane that never stamps
 * needs its producer fixed, and one word for both hides the one that needs a human.
 */
function writerLabel(w: AdminHiveWriter): string {
  if (w.kind === 'stamped') return w.version;
  return t(
    w.kind === 'pre_stamp' ? 'admin.hive.writer_pre_stamp' : 'admin.hive.writer_no_provenance',
  );
}

/** One stat cell: the verdict, and the `n / d` that makes it checkable. */
function Stat(props: { label: string; stat: AdminHiveStat }): JSX.Element {
  return (
    <li class="card">
      <div class="card__content">
        <p class="metric-grid__label">{props.label}</p>
        <Show
          when={props.stat.kind === 'graded' ? props.stat : null}
          fallback={
            <p class="metric-grid__value">
              {props.stat.kind === 'not_on_contract'
                ? t('admin.hive.not_on_contract')
                : t('admin.hive.no_data')}
            </p>
          }
        >
          {(graded) => (
            <>
              <p class="metric-grid__value">{PCT.format(graded().rate)}</p>
              <p class="metric-grid__note">
                {t('admin.hive.of', {
                  n: NUM.format(graded().n),
                  d: NUM.format(graded().d),
                })}
              </p>
            </>
          )}
        </Show>
      </div>
    </li>
  );
}

/** One lane: its parse verdict, its alerts, then its eight rates. */
function Lane(props: { lane: AdminHiveLane }): JSX.Element {
  const label = () => laneLabel(props.lane.lane);
  return (
    <section class="admin-band">
      <h3>
        <Kicker as="span">{label()}</Kicker>
      </h3>

      {/* `events: 0` is "nobody ran" — a legible answer, and a different one from
          "everybody ran and nothing spoke the contract" (`no_row_parsed` below). */}
      <Show
        when={props.lane.events > 0}
        fallback={<EmptyState message={t('admin.hive.lane_silent')} />}
      >
        <p class="metric-grid__note">
          {t('admin.hive.lane_parsed', {
            parsed: NUM.format(props.lane.parsed),
            events: NUM.format(props.lane.events),
            rounds: NUM.format(props.lane.rounds),
            dispatched: NUM.format(props.lane.denominators.dispatched),
          })}
        </p>

        {/* The refused rows are NAMED, never folded into a zero. Prod measured four
            generations of `team_fanout` inside one 36-hour window, so an old-generation
            row is history with a signature and a date — not a rate. */}
        <Show when={props.lane.unparseable > 0}>
          <p class="metric-grid__note">
            {t('admin.hive.unparseable', {
              n: NUM.format(props.lane.unparseable),
              rate: PCT.format(props.lane.unparseable_rate),
            })}
          </p>
          <ul class="admin-hive__reasons">
            <For each={props.lane.by_version}>
              {(bucket) => (
                <>
                  <For each={bucket.reasons}>
                    {(reason) => (
                      <li class="admin-hive__reason">
                        <code>{writerLabel(bucket.writer)}</code> {reason.reason}{' '}
                        {t('admin.hive.reason_last', {
                          n: NUM.format(reason.events),
                          at: absolute(reason.last_at_iso),
                        })}
                      </li>
                    )}
                  </For>
                  {/* A capped signature list that says nothing renders as a COMPLETE
                      diagnosis of the refusals, which is the exact defect this band
                      exists to prevent: every number beside its own denominator, and a
                      truncation stated rather than silent. */}
                  <Show when={bucket.reasons_truncated > 0}>
                    <li class="admin-hive__reason">
                      <code>{writerLabel(bucket.writer)}</code>{' '}
                      {t('admin.hive.reasons_truncated', {
                        n: NUM.format(bucket.reasons_truncated),
                      })}
                    </li>
                  </Show>
                </>
              )}
            </For>
          </ul>
        </Show>

        <Show when={props.lane.alerts.length > 0}>
          <ul class="admin-hive__alerts" aria-label={t('admin.hive.alerts_for', { lane: label() })}>
            <For each={props.lane.alerts}>
              {(alert) => (
                // `role="alert"` on a CHILD of the `<li>`, never the `<li>` itself:
                // the role replaces the item's `listitem` semantics, which leaves
                // the `<ul>` above with no list children at all — axe reports it as
                // `list [serious]` plus `aria-allowed-role`, and a screen reader
                // stops announcing "list, 2 items". The child is the house alert
                // (spec §3.4): a destructive hairline and text,
                // never a fill.
                <li data-alert={alert.kind}>
                  <div class="alert alert--icon alert--destructive" role="alert">
                    <IconAlertCircle />
                    <p class="alert__title">{alertText(alert)}</p>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>

        <ul class="metric-grid metric-grid--5" aria-label={label()}>
          <Stat label={t('admin.hive.stat_multi_member')} stat={props.lane.multi_member_rate} />
          <Stat label={t('admin.hive.stat_idle')} stat={props.lane.idle_rate} />
          <Stat label={t('admin.hive.stat_steerable')} stat={props.lane.steerable_rate} />
          <Stat label={t('admin.hive.stat_steered')} stat={props.lane.steered_rate} />
          <Stat label={t('admin.hive.stat_attempts')} stat={props.lane.attempts_rate} />
          <Stat label={t('admin.hive.stat_falsified')} stat={props.lane.falsified_rate} />
          <Stat label={t('admin.hive.stat_no_work')} stat={props.lane.no_work_rate} />
          <Stat label={t('admin.hive.stat_unrouted')} stat={props.lane.unrouted_handoff_rate} />
        </ul>
      </Show>
    </section>
  );
}

/**
 * The band head, then each lane as its own block. A FRAGMENT, like `AdminStatBand`:
 * the container's `<QueryBoundary/>` wraps it in `.page`, so every lane takes the
 * page's one vertical rhythm (spec §5) rather than a sibling margin of its own.
 */
export function AdminHiveBand(props: { hive: AdminHive }): JSX.Element {
  return (
    <>
      <section class="admin-band">
        <h2>
          <Kicker as="span">{t('admin.hive.title')}</Kicker>
        </h2>
        <p class="metric-grid__note">{t('admin.hive.window', { days: props.hive.window_days })}</p>
      </section>
      <Show
        when={props.hive.lanes.length > 0}
        fallback={<EmptyState message={t('admin.hive.no_lanes')} />}
      >
        <For each={props.hive.lanes}>{(lane) => <Lane lane={lane} />}</For>
      </Show>
    </>
  );
}
